import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Star } from "lucide-react";

import { imageUrl } from "@/features/imageUpload";

export interface PhotoItem {
  /** Stable id used for ordering. For per-card photos this is the image ref;
   *  for the bundle panel it's a sentinel so the slot survives regeneration. */
  id: string;
  /** Current image ref to render / upload. */
  ref: string;
  kind: "panel" | "front" | "back";
  label: string;
}

interface PhotoOrdererProps {
  items: PhotoItem[];
  /** Display order of item ids. */
  order: string[];
  included: Set<string>;
  max?: number;
  onReorder: (order: string[]) => void;
  onToggle: (id: string) => void;
}

const KIND_LABEL: Record<PhotoItem["kind"], string> = {
  panel: "Panel",
  front: "Front",
  back: "Back",
};

export function PhotoOrderer({
  items,
  order,
  included,
  max = 12,
  onReorder,
  onToggle,
}: PhotoOrdererProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const itemById = new Map(items.map((i) => [i.id, i]));
  const ordered = order
    .map((id) => itemById.get(id))
    .filter((i): i is PhotoItem => i !== undefined);

  // First included item in display order is eBay's gallery thumbnail.
  const galleryId = ordered.find((i) => included.has(i.id))?.id;
  const includedCount = ordered.filter((i) => included.has(i.id)).length;
  const atCap = includedCount >= max;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(String(active.id));
    const newIndex = order.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(order, oldIndex, newIndex));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">
          Drag to reorder · first photo is the gallery image
        </p>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {includedCount}/{max}
        </span>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={horizontalListSortingStrategy}>
          <div className="flex flex-wrap gap-2">
            {ordered.map((item) => (
              <SortablePhoto
                key={item.id}
                item={item}
                isIncluded={included.has(item.id)}
                isGallery={item.id === galleryId}
                disabledByCap={atCap && !included.has(item.id)}
                onToggle={() => onToggle(item.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortablePhoto({
  item,
  isIncluded,
  isGallery,
  disabledByCap,
  onToggle,
}: {
  item: PhotoItem;
  isIncluded: boolean;
  isGallery: boolean;
  disabledByCap: boolean;
  onToggle: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative w-[84px] shrink-0 rounded-md border bg-card p-1 ${
        isGallery ? "border-primary" : "border-border"
      } ${isDragging ? "z-10 opacity-80 shadow-lg" : ""} ${
        isIncluded ? "" : "opacity-50"
      }`}
    >
      <div className="relative">
        <img
          src={imageUrl(item.ref) ?? undefined}
          alt={item.label}
          className="h-[84px] w-full rounded object-cover"
        />
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          className="absolute left-0.5 top-0.5 cursor-grab rounded bg-black/55 p-0.5 text-white active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        {isGallery && isIncluded && (
          <span className="absolute right-0.5 top-0.5 inline-flex items-center gap-0.5 rounded bg-primary px-1 py-0.5 text-[8px] font-bold uppercase text-primary-foreground">
            <Star className="h-2.5 w-2.5" />
            Gallery
          </span>
        )}
        <span className="absolute bottom-0.5 left-0.5 rounded bg-black/55 px-1 text-[8px] font-medium text-white">
          {KIND_LABEL[item.kind]}
        </span>
      </div>
      <label
        className={`mt-1 flex items-center justify-center gap-1 text-[10px] ${
          disabledByCap ? "text-muted-foreground/60" : "text-foreground"
        }`}
        title={disabledByCap ? "Remove one to add this" : undefined}
      >
        <input
          type="checkbox"
          checked={isIncluded}
          disabled={disabledByCap}
          onChange={onToggle}
          className="h-3 w-3"
        />
        {isIncluded ? "Listed" : disabledByCap ? "Max" : "Off"}
      </label>
    </div>
  );
}
